import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import { fetchAdminIntegrationLogs, fetchAdminIntegrationLogDetail } from '../../../services/Http/AdminApi';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminIntegrationLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState('running');
  const loadRequest = useRef(0);
  const [search, setSearch] = useState('');
  const [logType, setLogType] = useState('');
  const [period, setPeriod] = useState('30d');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setFetchStatus('running');
    setLoading(true);
    try {
      const resp = await fetchAdminIntegrationLogs({ search, log_type: logType, period, per_page: 50 });
      if (request !== loadRequest.current) return;
      setRows(resp?.data || []);
      setFetchStatus('success');
    } catch (error) {
      if (request !== loadRequest.current) return;
      setFetchStatus('failed');
      toast.error(error?.response?.data?.message || 'Failed to load integration logs');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [search, logType, period]);

  useEffect(() => { load(); return () => { loadRequest.current += 1; }; }, [load]);

  const openDetail = async (row) => {
    setSelected({ row, detail: null });
    setDetailLoading(true);
    try {
      const resp = await fetchAdminIntegrationLogDetail(row.id);
      setSelected({ row, detail: resp?.data || null });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load log detail');
      setSelected({ row, detail: null });
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { key: 'created_at', label: 'Time', render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—') },
    { key: 'organization_name', label: 'Organization' },
    { key: 'integrator_name', label: 'Integrator' },
    { key: 'integration_type', label: 'Type' },
    { key: 'log_type', label: 'Log Type' },
    { key: 'method', label: 'Method' },
    { key: 'endpoint', label: 'Endpoint', render: (r) => <CodeEllipsis title={r.endpoint}>{r.endpoint || '—'}</CodeEllipsis> },
    { key: 'status_code', label: 'Status' },
    { key: 'duration_ms', label: 'Duration (ms)' },
    { key: 'actions', label: 'Actions', render: (r) => <InlineBtn type="button" onClick={() => openDetail(r)}>Inspect</InlineBtn> },
  ]), []);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Integration Logs</PageTitle></PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <AdminFilterBar>
          <FilterRow>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search org / integrator / endpoint / error" />
            <Select value={logType} onChange={(e) => setLogType(e.target.value)}>
              <option value="">All log types</option>
              <option value="request">request</option>
              <option value="response">response</option>
              <option value="error">error</option>
              <option value="webhook">webhook</option>
            </Select>
            <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </Select>
          </FilterRow>
          <FilterRow>
            <FreshnessBadge status={fetchStatus} sourceType="operational" />
            <MainButton size="small" onClick={load}>Refresh</MainButton>
          </FilterRow>
        </AdminFilterBar>

        {fetchStatus === 'failed' ? <p role="alert">Failed to load integration logs</p> : <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No integration logs found" />}

        <StandardModal
          isOpen={Boolean(selected)}
          onClose={() => setSelected(null)}
          title="Integration Log Detail"
          width="960px"
        >
          {detailLoading ? <p>Loading log detail...</p> : (
            <DetailWrap>
              <MetaGrid>
                <MetaItem><strong>ID</strong><code>{selected?.detail?.id || selected?.row?.id}</code></MetaItem>
                <MetaItem><strong>Type</strong><span>{selected?.detail?.log_type || selected?.row?.log_type || '—'}</span></MetaItem>
                <MetaItem><strong>Status Code</strong><span>{selected?.detail?.status_code ?? selected?.row?.status_code ?? '—'}</span></MetaItem>
                <MetaItem><strong>Duration</strong><span>{selected?.detail?.duration_ms ?? selected?.row?.duration_ms ?? '—'}</span></MetaItem>
              </MetaGrid>
              <PayloadSection>
                <h4>Request Body (redacted)</h4>
                <PayloadPanel>{pretty(selected?.detail?.request_body)}</PayloadPanel>
              </PayloadSection>
              <PayloadSection>
                <h4>Response Body (redacted)</h4>
                <PayloadPanel>{pretty(selected?.detail?.response_body)}</PayloadPanel>
              </PayloadSection>
              <PayloadSection>
                <h4>Error Message</h4>
                <PayloadPanel>{pretty(selected?.detail?.error_message)}</PayloadPanel>
              </PayloadSection>
            </DetailWrap>
          )}
        </StandardModal>
      </PageContent>
    </PageContainer>
  );
};

export default AdminIntegrationLogs;

const pretty = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

const FilterRow = styled.div`display:flex;gap: 0.625rem;align-items:center;flex-wrap:wrap;`;
const SearchInput = styled.input`width:320px;border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const Select = styled(SimpleSelect)`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const InlineBtn = styled.button`border:1px solid ${({theme})=>theme.colors.border};background:${({theme})=>theme.colors.surface};padding: 0.375rem 0.625rem;border-radius:8px;cursor:pointer;font-weight:600;color:${({theme})=>theme.colors.textPrimary}; &:hover{color:var(--color-primary);border-color:var(--color-primary);}`;
const CodeEllipsis = styled.code`display:inline-block;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size: 0.75rem;`;
const DetailWrap = styled.div`display:flex;flex-direction:column;gap: 0.875rem;`;
const MetaGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap: 0.625rem;`;
const MetaItem = styled.div`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.625rem;background:${({theme})=>theme.colors.surfaceMuted}; display:flex;flex-direction:column;gap: 0.25rem; code{font-size: 0.75rem;word-break:break-all;}`;
const PayloadSection = styled.section`h4{margin: 0 0 0.5rem;color:${({theme})=>theme.colors.textPrimary};font-size: 0.875rem;}`;
const PayloadPanel = styled.pre`margin:0;padding: 0.75rem;border-radius:10px;border:1px solid ${({theme})=>theme.colors.border};background:${({theme})=>theme.colors.surface};color:${({theme})=>theme.colors.textPrimary};font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size: 0.75rem;white-space:pre-wrap;overflow:auto;max-height:220px;`;
